import React from "react";
import { atom, useAtomValue } from "jotai";
import { ArrowsPointingOutIcon, BellIcon, SparklesIcon } from "@heroicons/react/24/outline";
import { BrandIcons, Flyout, Link, Tab, TabsContainer, Translate } from "../../UI";
import { RouteConstants } from "../../../Routes";
import { WhatsNewTimeline } from "../../../pages/release-notes/WhatsNewTimeline.component";
import { NotificationsPanel } from "../../../pages/release-notes/NotificationsPanel.component";

const NotificationsIcon = BrandIcons.notifications;

/**
 * Tab selection for the header panel only. Deliberately NOT the page's
 * `releaseNotesTabAtom`: sharing it would make the dropdown and the full page
 * yank each other's tab around, and the page syncs its atom to `?tab=`.
 */
const headerUpdatesTabAtom = atom<number>(0);

const TAB_ROUTES = [
    RouteConstants.RELEASE_NOTES + '?tab=whats-new',
    RouteConstants.RELEASE_NOTES + '?tab=notifications'
];

// #region HeaderNotificationsMenu
/**
 * Bell dropdown sitting immediately left of the account menu. It reuses the
 * exact What's New / Notifications components the `/release-notes` page renders,
 * so there is one implementation of each feed rather than a compact fork that
 * drifts. The expand control opens the active tab full screen.
 */
export const HeaderNotificationsMenu: React.FunctionComponent = (): JSX.Element => {
    const closeRef = React.useRef<(() => void) | null>(null);
    const activeTab = useAtomValue(headerUpdatesTabAtom);

    return (
        <Flyout
            title=""
            icon={BellIcon}
            closeRef={closeRef}
            buttonClassName="!px-1.5 !py-1.5"
            panelClassName="w-[24rem] sm:w-[30rem]"
            wrapList={false}
            hideArrow={true}
            buttonContent={<NotificationsIcon className="h-6 w-6 shrink-0 text-brand dark:text-brand-dark" />}
        >
            <div className="flex items-center justify-between gap-x-2 px-2 pt-1 pb-2">
                <span className="text-[13px] font-semibold dark:text-white text-black">
                    <Translate translation_key="NAVIGATION.NOTIFICATIONS" fallback="Notifications" />
                </span>
                <Link
                    href={TAB_ROUTES[activeTab] ?? TAB_ROUTES[0]}
                    action={() => closeRef.current?.()}
                    className="inline-flex items-center gap-x-1.5 px-2 py-1 rounded-md text-xs font-semibold dark:text-gray-200 text-gray-600 dark:hover:bg-stone-700 hover:bg-surface-strong cursor-pointer"
                    title="Open full screen"
                >
                    <ArrowsPointingOutIcon className="h-4 w-4 shrink-0 text-brand dark:text-brand-dark" />
                    <span>Open full screen</span>
                </Link>
            </div>

            <TabsContainer stateAtom={headerUpdatesTabAtom}>
                <Tab key="whats-new" tabName="What's New" tabIcon={SparklesIcon} bodyClassName="p-0">
                    <div className="max-h-[26rem] overflow-y-auto px-2">
                        <WhatsNewTimeline />
                    </div>
                </Tab>
                <Tab key="notifications" tabName="Notifications" tabIcon={NotificationsIcon} bodyClassName="p-0">
                    <div className="max-h-[26rem] overflow-y-auto px-2 py-2">
                        <NotificationsPanel />
                    </div>
                </Tab>
            </TabsContainer>
        </Flyout>
    );
};
